import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Message from "../components/LoadingError/Error";
import Loading from "../components/LoadingError/Loading";
import Header from "./../components/Header";
import { login, googleLogin } from "./../Redux/Actions/userActions";
import { guestLogin } from "./../Redux/Actions/guestActions";
import "./login.css";

const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = () => {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const nonceRef = useRef(crypto.randomUUID());

  const dispatch  = useDispatch();
  const location  = useLocation();
  const navigate  = useNavigate();
  const redirect  = location.search ? location.search.split("=")[1] : "/";

  // Only show guest option when coming from checkout
  const isCheckoutRedirect = redirect === "shipping";

  const userLogin = useSelector((state) => state.userLogin);
  const { error: loginError, loading: loginLoading, userInfo: loginUserInfo } = userLogin;

  const userGoogleLogin = useSelector((state) => state.userGoogleLogin);
  const { error: googleError, loading: googleLoading } = userGoogleLogin;

  const userInfo  = loginUserInfo;
  const isLoading = loginLoading || googleLoading;
  const error     = loginError   || googleError;

  const handleGoogleResponse = useCallback(
    (response) => dispatch(googleLogin(response.credential, nonceRef.current)),
    [dispatch]
  );

  const handleGuestContinue = () => {
    dispatch(guestLogin());
    navigate("/shipping");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!GOOGLE_CLIENT_ID) {
      console.error("Google Client ID is not configured");
      return;
    }

    let script = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    const initGoogle = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id:   GOOGLE_CLIENT_ID,
        callback:    handleGoogleResponse,
        nonce:       nonceRef.current,
        auto_select: false,
      });
      const btn = document.getElementById("googleSignInButton");
      if (btn) {
        window.google.accounts.id.renderButton(btn, {
          theme: "outline",
          size:  "medium",
          width: 332,
          text:  "signin_with",
        });
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.src   = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      script.onload = initGoogle;
    } else if (window.google) {
      initGoogle();
    } else {
      script.addEventListener("load", initGoogle);
      return () => script.removeEventListener("load", initGoogle);
    }

    return () => {
      const btn = document.getElementById("googleSignInButton");
      if (btn) btn.innerHTML = "";
    };
  }, [handleGoogleResponse]);

  useEffect(() => {
    if (userInfo) navigate(redirect === "/" ? "/" : `/${redirect}`);
  }, [userInfo, navigate, redirect]);

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(login(email, password));
  };

  return (
    <>
      <Header />
      <div className="login-center">

        <div className="login-message-slot">
          {error     && <Message variant="alert-danger">{error}</Message>}
          {isLoading && <Loading />}
        </div>

        <form
          className="Login"
          onSubmit={submitHandler}
          style={{ opacity: isLoading ? 0.6 : 1, transition: "opacity 0.2s ease" }}
        >
          <p className="login-card-title">Welcome back</p>

          {/* ── Google ── */}
          <div className="google-btn-wrapper">
            <div
              id="googleSignInButton"
              style={{
                opacity:       isLoading ? 0.4 : 1,
                pointerEvents: isLoading ? "none" : "auto",
                transition:    "opacity 0.2s ease",
              }}
            />
          </div>

          <div className="login-divider">or sign in with email</div>

          {/* ── Email fields ── */}
          <input
            type="email"
            placeholder="Email"
            value={email}
            disabled={isLoading}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            disabled={isLoading}
            onChange={(e) => setPassword(e.target.value)}
          />

          {/* ── Action buttons ── */}
          <div className="login-actions">
            {/* 1. Primary: Login */}
            <button type="submit" className="btn-primary-login" disabled={isLoading}>
              {isLoading ? "Signing in…" : "Sign In"}
            </button>

            {/* 2. Tertiary: Create Account (outlined, neutral) */}
            <Link
              to={redirect ? `/register?redirect=${redirect}` : "/register"}
              className="btn-create-account"
            >
              Create Account
            </Link>

            {/* 3. Secondary: Guest (only at checkout, clearly differentiated) */}
            {isCheckoutRedirect && (
              <div className="guest-section">
                <div className="login-divider">or</div>
                <p className="guest-label">No account? Skip it — checkout as a guest.</p>
                <button
                  type="button"
                  className="btn-guest"
                  disabled={isLoading}
                  onClick={handleGuestContinue}
                >
                  Continue as Guest
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default Login;